import type { FormIdAndVersion } from "@thalamos/aspire-common";

/** One form version's parts, as much of them as the viewer needs. */
type ViewablePartData = {
  data?: { disclosureHarmful?: boolean | null } | null;
}[];

/**
 * What the PDF viewer needs of a form. Structural so that both the assessment
 * page's forms and a work item's — which carry no per-version detail — can be
 * shown by the same dialog.
 */
export type ViewableForm = {
  id: string;
  formTemplate: FormIdAndVersion;
  latestVersion: number;
  versions: {
    version: number;
    pdfUri?: string | null;
    data?: ViewablePartData;
  }[];
  allDrafts?: { data?: { disclosureHarmful?: boolean | null } | null }[];
};

export type ViewableVersion = { version: number; data: ViewablePartData };

export type PdfViewerBlocks = {
  /**
   * Ask the endpoint for the form without naming a version. It answers with
   * whatever it can: the live draft for the SOAD, or every stored version
   * merged into one document.
   */
  unversioned: boolean;
  /** Versions to show separately, newest first — the order the endpoint merges. */
  versions: ViewableVersion[];
};

/**
 * Which blocks the viewer shows for a form.
 *
 * The endpoint decides what an unnamed version resolves to, so this mirrors
 * that decision rather than making its own: a SOAD looking at a version nobody
 * has signed is served their live draft, and asking for the versions it
 * supersedes as well would show them work they are not there to check.
 * Everyone else only ever sees stored versions.
 */
export function pdfViewerBlocks(
  form: ViewableForm,
  isSoad: boolean,
): PdfViewerBlocks {
  // A stored PDF is what makes a version retrievable on its own, so that is the
  // test rather than the version's status.
  const stored = form.versions
    .flatMap((version) =>
      version.pdfUri
        ? [{ version: version.version, data: version.data ?? [] }]
        : [],
    )
    .sort((a, b) => b.version - a.version);

  const latestIsStored = stored.some((v) => v.version === form.latestVersion);

  if (isSoad && !latestIsStored) {
    return { unversioned: true, versions: [] };
  }

  // Nothing is separately retrievable, so fall back to asking for the form:
  // either it has no signed version yet, or the caller carries no per-version
  // detail to name one with.
  if (stored.length === 0) {
    return { unversioned: true, versions: [] };
  }

  return { unversioned: false, versions: stored };
}
